import * as React from 'react';
import {FormEvent} from 'react';
import Button from "@components/common/button/Button";
import Typography from '@material-ui/core/Typography';
import CalendarTemplates from '@constants/calendarTemplates';
import TranslateNew from '@translate/TranslateNew';
import {WithStyles, withStyles} from '@material-ui/core/styles';
import {GuideStyles} from '../guide.styles';
import ProgressBar from "@components/common/progressBar/progressBar";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RadioAccordion from "@components/common/radioAccordion/radioAccordion.component";
import clsx from "clsx";
import TemplateCalendar from "@components/guide/components/templateCalendar.component";
import {IFamilyMember} from "@app-types/websocket/family.types";
import { primaryColor, secondaryColor } from 'src/main.style';
import { TemplateName } from '@app-types/websocket/schedule.types';

const tn: TranslateNew = TranslateNew.getInstance();

interface IStepTemplateProps extends WithStyles<typeof GuideStyles> {
    setTemplate: (template: boolean[]) => void;
    setStep: (step: number) => void;
    step: number;
    progress?: number | undefined;
    toggleSnackBar: (state: boolean) => void;
    setStepErrorType: (type: string) => void;
    goBack: () => void;
    secondParent: IFamilyMember;
    firstParent: IFamilyMember;
    style?:React.CSSProperties;
    localization: 'en'|'se';
}

interface IStepTemplateState {
    formValues: {
        template?: boolean[] | undefined;
    };
    formErrors: {
        template?: string | undefined,
    };
}

class StepTemplate extends React.Component<IStepTemplateProps, IStepTemplateState> {
    constructor(props: any) {
        super(props);

        this.state = {
            formValues: {
                template: CalendarTemplates[0].days,
            },
            formErrors: {
                template: undefined,
            },
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.validateTemplate = this.validateTemplate.bind(this);
        this.resetToDefault = this.resetToDefault.bind(this);
    }

    public render() {
        const {formValues, formErrors} = this.state;
        const {classes: style, step, firstParent, secondParent} = this.props;

        return (
            <div className={style.container}  style={this.props.style}>
                <div className={clsx([style.topBar, style.topBarWithBackButton])}>
                    {this.props.progress && (<ProgressBar value={this.props.progress + step}/>)}
                    <Button variant="text" onClick={this.resetToDefault} className={style.backButton}>
                        <ArrowBackIcon fontSize="small"/>
                        <span style={{flex: 1}}>{tn.lang.back}</span>
                    </Button>
                </div>
                <div className={style.topContent}>
                    <Typography variant="h2">{tn.lang.newCalendarTemplateTitle}</Typography>
                    <Typography>
                        {tn.lang.newCalendarTemplateSubTitle}
                    </Typography>
                </div>
                <form className={style.formGuide} onSubmit={this.handleSubmit}>
                    <div className={style.formMiddleContent}>
                        <div className={style.templateList}>
                            {CalendarTemplates
                                .map((template, index) => (
                                    <div key={`template-${template.name}-${template.type}`}>
                                        <RadioAccordion
                                            key={`radio-template${template.name}`}
                                            name="template"
                                            value={index}
                                            checked={formValues.template === template.days}
                                            label={tn.lang.calendarTemplateName(template.name)}
                                            onChange={this.handleInputChange}
                                            error={formErrors.template}
                                            helpInfo={
                                                <div className={style.templateHelpInfo}>
                                                    {template.name === TemplateName.CUSTOM
                                                    ?<span>{tn.lang.calWizardCustomExplain}</span>
                                                    :<TemplateCalendar
                                                        days={template.days}
                                                        primaryColor={primaryColor}// {firstParent.color}
                                                        secondaryColor={secondaryColor}// {secondParent.color}
                                                        monthView={true}
                                                        localization={this.props.localization}
                                                    />}
                                                    
                                                    {/* template.description */}
                                                </div>
                                            }
                                        />
                                    </div>
                                ))
                            }
                        </div>
                        {formErrors.template &&
                            <Typography className={style.errorMessage}>{formErrors.template}</Typography>}
                    </div>
                    <div className={style.formBottomContent}>
                        <Button
                            type="submit"
                            variant='contained'
                            color='primary'
                            size="large"
                            fullWidth={true}
                        >
                            {tn.lang.continue}
                        </Button>
                    </div>
                </form>
            </div>
        );
    }

    private handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const {name, value, type, checked} = event.target;
        const inputValue = type === 'checkbox' ? checked : value;

        switch (name) {
            case 'template':
                const {formValues} = this.state;
                const calendarTemplate = CalendarTemplates[Number(value)];
                if (!calendarTemplate) return;
                const {days} = calendarTemplate;

                this.setState((prevState) => ({
                    formValues: {
                        ...prevState.formValues,
                        [name]: days,
                    },
                    formErrors: {
                        ...prevState.formErrors,
                        [name]: undefined,
                    }
                }));
                break;
        }
    }

    private handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const {setTemplate, toggleSnackBar, setStepErrorType, setStep} = this.props;
        const {formValues} = this.state;
        const isTemplateValid = this.validateTemplate();

        if (isTemplateValid) {
            try {
                if (formValues.template && formValues.template.length > 0) {
                    setTemplate(formValues.template);
                    setStep(4);
                } else {
                    setStep(3);
                }

            } catch (error) {
                toggleSnackBar(true);
                setStepErrorType(error);
            }
        }
    }

    private validateTemplate = (): boolean => {
        const {formValues} = this.state;

        if (!formValues.template) {
            this.setState((prevState) => ({
                formErrors: {
                    ...prevState.formErrors,
                    template: tn.lang.CalendarTemplatesErrorTemplate,
                },
            }));
            return false;
        }

        this.setState((prevState) => ({
            formErrors: {
                ...prevState.formErrors,
                template: undefined,
            },
        }));
        return true;
    }

    private resetToDefault = () => {
        const {setStep} = this.props;
        this.setState(
            {
                formValues: {
                    template: undefined,
                },
                formErrors: {
                    template: undefined,
                },
            }
        );

        if (this.props.goBack) {
            this.props.goBack();
        } else {
            setStep(2);
        }
    }
}


export default withStyles(GuideStyles)(StepTemplate);



// WEBPACK FOOTER //
// ./src/components/guide/steps/stepTemplate.component.tsx